import {
	type ComponentType,
	createContext,
	type PropsWithChildren,
	type ReactNode,
	useContext,
} from 'react';

import { type ExtensionParams } from '@atlaskit/editor-common/extensions';
import { type MediaProvider } from '@atlaskit/editor-common/provider-factory';
import { Auth } from '@atlaskit/media-core';
import { UploadRejectionData } from '@atlaskit/media-picker';

export type Mode = 'renderer' | 'editor' | 'pdf';

export interface ExperienceTracker {
	start: ({ analyticsSource }: { analyticsSource: string }) => void;
	succeed: () => void;
	stopOnError: ({ error }: { error: Error }) => void;
}

export interface ConfluenceDependenciesProviderProps {
	mode: Mode;
	extensionNode?: ExtensionParams<Record<string, any>>;
	contentId: string;
	children?: ReactNode;
}

interface Flag {
	id: string;
	title: string;
	description: string;
}

export interface ConfluenceDependenciesProviderResponse {
	cloudId: string;
	displayName: string;
	isAdminHubAIEnabled: boolean;
	orgId: string;
	userId: string;
	submitError?: (error: Error) => void;
	currentDataRefHref?: string;
	errorBoundaryComponent?: ComponentType<PropsWithChildren<{}>>;
	experienceStartComponent?: ComponentType<PropsWithChildren<{}>>;
	experienceSuccessComponent: ComponentType<{}>;
	experienceStopComponent: ComponentType<{ error?: Error }>;
	lazyEmojiComponentLoader: any;
	createRendererMediaProvider?: (options: {
		contentId: string;
		isExternalShare: boolean;
	}) => Promise<MediaProvider>;
	flags?: {
		showFlag?: (flag: Flag) => void;
		showErrorFlag?: (flag: Flag) => void;
	};

	// Custom search dependencies
	getSpaceIdsFromSpaceKeys: (spaceKeys: string[]) => Promise<string[]>;
	advancedSearchUrl: string;
	peopleDirectoryUrl: string;

	// Config panel media upload
	createEditorMediaProvider?: (options: {
		contentId: string;
		isExternalShare: boolean;
		contentType: string;
		onUploadRejection?: ((data: UploadRejectionData) => boolean) | undefined;
	}) => Promise<MediaProvider>;
	getAttachmentSizeLimitErrorFlag?: any;
	mediaUploadExperienceTracker?: ExperienceTracker;
}

export const ConfluenceDependenciesContext = createContext<ConfluenceDependenciesProviderResponse>({
	experienceSuccessComponent: () => null,
	experienceStopComponent: () => null,
	createRendererMediaProvider: undefined,
	cloudId: '',
	displayName: '',
	isAdminHubAIEnabled: false,
	orgId: '',
	userId: '',
	advancedSearchUrl: '',
	peopleDirectoryUrl: '',
	flags: undefined,
	submitError: () => {},
	getSpaceIdsFromSpaceKeys: async () => [],
	createEditorMediaProvider: () =>
		Promise.resolve({
			viewMediaClientConfig: {
				authProvider: async () => ({}) as Auth,
			},
			uploadMediaClientConfig: {
				authProvider: async () => ({}) as Auth,
			},
		} as MediaProvider),
	getAttachmentSizeLimitErrorFlag: undefined,
	mediaUploadExperienceTracker: undefined,
	lazyEmojiComponentLoader: () => null,
	currentDataRefHref: '',
});

export function useConfluenceDependencies(): ConfluenceDependenciesProviderResponse {
	const ctx = useContext<ConfluenceDependenciesProviderResponse>(ConfluenceDependenciesContext);
	if (!ctx) {
		throw new Error('useConfluenceDependencies must be used within ConfluenceDependenciesProvider');
	}
	return ctx;
}
